import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { BarChart } from '../components/charts/BarChart.jsx';
import { ChartCard } from '../components/charts/ChartCard.jsx';
import { LineChart } from '../components/charts/LineChart.jsx';
import { useChartPalette } from '../components/charts/useChartPalette.js';
import { Button } from '../components/ui/Button.jsx';
import { MetricCard } from '../components/ui/Card.jsx';
import { EmptyState } from '../components/ui/EmptyState.jsx';
import { Skeleton } from '../components/ui/Skeleton.jsx';
import { Spinner } from '../components/ui/Spinner.jsx';
import { useTranslation } from '../i18n/useTranslation.js';
import { getPreferences } from '../services/authApi.js';
import { financeApi } from '../services/financeApi.js';
import { salesApi } from '../services/salesApi.js';
import { formatCurrency } from '../utils/formatters.js';

import { ActionCenter } from './ActionCenter.jsx';
import { DashboardConfigModal } from './DashboardConfigModal.jsx';
import { toAgingSeries, toStatusSeries, toTrendSeries } from './finance/financeCharts.js';

export function Dashboard() {
  const { t } = useTranslation();
  const palette = useChartPalette();
  const [configOpen, setConfigOpen] = useState(false);
  const prefs = useQuery({ queryKey: ['preferences'], queryFn: getPreferences });
  const enabledList = prefs.data?.dashboardWidgets;
  const isOn = (key) => !Array.isArray(enabledList) || enabledList.includes(key);
  const dashboard = useQuery({
    queryKey: ['finance', 'dashboard'],
    queryFn: () => financeApi.dashboard().then((r) => r.items),
  });
  const trend = useQuery({
    queryKey: ['finance', 'timeseries', 12],
    queryFn: () => financeApi.timeseries({ months: 12 }).then((r) => r.items),
  });
  const balances = useQuery({
    queryKey: ['finance', 'balance-history', 12],
    queryFn: () => financeApi.balanceHistory({ months: 12 }).then((r) => r.items),
  });
  const funnel = useQuery({
    queryKey: ['sales', 'funnel'],
    queryFn: () => salesApi.funnel().then((r) => r.items || []),
  });
  const agingAr = useQuery({
    queryKey: ['finance', 'aging', 'AR'],
    queryFn: () => financeApi.aging({ type: 'AR' }).then((r) => r.items),
  });
  const status = useQuery({
    queryKey: ['finance', 'invoice-status'],
    queryFn: () => financeApi.invoiceStatus().then((r) => r.items),
  });

  // Stable array identities so the memoized sparklines don't redraw on unrelated renders.
  const trendSeries = useMemo(() => toTrendSeries(trend.data?.months), [trend.data]);

  if (dashboard.isLoading) {
    return (
      <section className="page">
        <Skeleton variant="cards" count={6} />
      </section>
    );
  }
  if (dashboard.isError) {
    return (
      <section className="page">
        <div className="alert danger">{dashboard.error?.message}</div>
      </section>
    );
  }

  const metrics = dashboard.data?.metrics || {};
  const invoices = dashboard.data?.invoices || {};
  const toNums = (arr) => (Array.isArray(arr) ? arr.map(Number) : []);
  const cashSeries = toNums(balances.data?.cash);
  const arSeries = toNums(balances.data?.accountsReceivable);
  const apSeries = toNums(balances.data?.accountsPayable);
  const funnelRows = (funnel.data || []).filter(
    (f) => f.stage !== 'CLOSED_LOST' && f.stage !== 'CLOSED_WON',
  );
  const weighted = funnelRows.reduce((acc, f) => acc + Number(f.weightedAmount || 0), 0);
  const agingSeries = toAgingSeries(agingAr.data?.buckets);
  const statusSeries = toStatusSeries(status.data?.statuses);
  const deltaFor = (series, { downIsGood = false } = {}) => {
    if (!Array.isArray(series) || series.length < 2) return undefined;
    const curr = Number(series[series.length - 1] || 0);
    const prev = Number(series[series.length - 2] || 0);
    if (!prev) return undefined;
    const pct = ((curr - prev) / Math.abs(prev)) * 100;
    const isFlat = Math.abs(pct) < 0.05; // rounds to ±0.0% - show as neutral, not green/red
    const direction = pct >= 0 ? 'up' : 'down';
    const good = downIsGood ? pct <= 0 : pct >= 0;
    return {
      value: `${pct >= 0 ? '+' : ''}${pct.toFixed(1)}% ${t('dashboard.deltaPrevMonth')}`,
      direction,
      variant: isFlat ? 'muted' : good ? 'success' : 'danger',
    };
  };
  const accent = palette?.accent || '#1d4ed8';
  const expense = palette?.expense || '#dc2626';
  const success = palette?.series?.[1] || '#059669';

  return (
    <section className="page">
      <header className="page-header">
        <div>
          <h1>{t('dashboard.title')}</h1>
          <p className="muted">{t('dashboard.subtitle')}</p>
        </div>
        <div className="page-actions">
          <Button variant="ghost" onClick={() => setConfigOpen(true)}>{t('dashboard.config.button')}</Button>
        </div>
      </header>

      <ActionCenter />

      <div className="card-grid">
        {isOn('revenue') && <MetricCard label={t('dashboard.revenue')}   value={formatCurrency(metrics.revenue)} to="/finance/overview"
          delta={deltaFor(trendSeries.revenue)} spark={trendSeries.revenue} />}
        {isOn('expenses') && <MetricCard label={t('dashboard.expenses')}  value={formatCurrency(metrics.expenses)} to="/finance/overview"
          delta={deltaFor(trendSeries.expenses, { downIsGood: true })} spark={trendSeries.expenses} />}
        {isOn('netProfit') && <MetricCard label={t('dashboard.netProfit')} value={formatCurrency(metrics.netProfit)} trendVariant="success" to="/finance/reports"
          delta={deltaFor(trendSeries.net)} spark={trendSeries.net} />}
        {isOn('cash') && <MetricCard label={t('dashboard.cash')}      value={formatCurrency(metrics.cash)} to="/finance/accounts"
          delta={deltaFor(cashSeries)} spark={cashSeries} />}
        {isOn('ar') && <MetricCard label={t('dashboard.ar')}        value={formatCurrency(metrics.accountsReceivable)} to="/finance/invoices"
          delta={deltaFor(arSeries, { downIsGood: true })} spark={arSeries} />}
        {isOn('ap') && <MetricCard label={t('dashboard.ap')}        value={formatCurrency(metrics.accountsPayable)} to="/finance/invoices"
          delta={deltaFor(apSeries, { downIsGood: true })} spark={apSeries} />}
        {isOn('openSales') && <MetricCard label={t('dashboard.openSales')} value={invoices.openSales ?? 0} to="/finance/invoices" />}
        {isOn('openPurchases') && <MetricCard label={t('dashboard.openPurchases')} value={invoices.openPurchases ?? 0} to="/finance/invoices" />}
        {isOn('forecast') && <MetricCard label={t('dashboard.forecast')}  value={formatCurrency(weighted)} to="/sales/pipeline" />}
        {isOn('overdue') && <MetricCard label={t('dashboard.overdue')} value={formatCurrency(invoices.overdueSalesAmount)} to="/finance/dunning"
          trendVariant={Number(invoices.overdueSalesAmount) > 0 ? 'danger' : 'success'} />}
      </div>

      <div className="dashboard-grid">
        {isOn('trendChart') && (
        <ChartCard className="span-2" title={t('dashboard.chartTitle')}>
          {trend.isLoading ? (
            <Spinner />
          ) : trendSeries.labels.length === 0 ? (
            <EmptyState message={t('dashboard.empty.trend')}>
              <Link className="btn" to="/finance/invoices">{t('dashboard.empty.trendCta')}</Link>
            </EmptyState>
          ) : (
            <LineChart
              valueKind="currency"
              labels={trendSeries.labels}
              datasets={[
                { label: t('dashboard.revenue'), data: trendSeries.revenue, color: accent, fill: true },
                { label: t('dashboard.expenses'), data: trendSeries.expenses, color: expense },
                { label: t('dashboard.netProfit'), data: trendSeries.net, color: success },
              ]}
            />
          )}
        </ChartCard>
        )}

        {isOn('pipelineChart') && (
        <ChartCard title={t('dashboard.pipeline')}>
          {funnel.isLoading ? (
            <Spinner />
          ) : funnelRows.length === 0 ? (
            <EmptyState message={t('dashboard.empty.pipeline')}>
              <Link className="btn" to="/sales/pipeline">{t('dashboard.empty.pipelineCta')}</Link>
            </EmptyState>
          ) : (
            <BarChart
              valueKind="currency"
              labels={funnelRows.map((f) => t(`sales.pipeline.stage.${f.stage}`))}
              datasets={[{ label: t('dashboard.forecast'), data: funnelRows.map((f) => Number(f.weightedAmount || 0)), color: accent }]}
            />
          )}
        </ChartCard>
        )}

        {isOn('agingChart') && (
        <ChartCard title={t('finance.chart.aging')}>
          {agingAr.isLoading ? (
            <Spinner />
          ) : agingSeries.labels.length === 0 || agingSeries.amounts.every((a) => !Number(a)) ? (
            <EmptyState message={t('dashboard.empty.agingGood')} />
          ) : (
            <BarChart
              valueKind="currency"
              labels={agingSeries.labels}
              datasets={[{ label: t('finance.chart.openAr'), data: agingSeries.amounts, color: accent }]}
            />
          )}
        </ChartCard>
        )}

        {isOn('statusChart') && (
        <ChartCard className="span-2" title={t('finance.chart.invoiceStatus')}>
          {status.isLoading ? (
            <Spinner />
          ) : statusSeries.labels.length === 0 ? (
            <EmptyState message={t('dashboard.empty.trend')}>
              <Link className="btn" to="/finance/invoices">{t('dashboard.empty.trendCta')}</Link>
            </EmptyState>
          ) : (
            <BarChart
              valueKind="count"
              labels={statusSeries.labels}
              datasets={[{ label: t('finance.chart.invoiceCount'), data: statusSeries.counts, color: accent }]}
            />
          )}
        </ChartCard>
        )}
      </div>

      <DashboardConfigModal open={configOpen} onClose={() => setConfigOpen(false)} enabledKeys={enabledList} />
    </section>
  );
}
